import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Snackbar,
  Tooltip,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import { API_ENDPOINTS } from "config/api";

const MAX_TOTAL_CHARS = 230;

const ProfileDescription = ({ studentId, token }) => {
  const [description, setDescription] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  useEffect(() => {
    if (!studentId || !token) return;

    axios
      .get(API_ENDPOINTS.PROFILE.GET_PROFILE_DESCRIPTION(studentId), {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setDescription(res.data?.profileDesc || "");
      })
      .catch((err) => {
        console.error("Failed to fetch profile description", err);
        setDescription("");
      });
  }, [studentId, token]);

  const handleSaveDescription = async () => {
    try {
      const res = await axios.post(
        API_ENDPOINTS.PROFILE.UPDATE_PROFILE_DESCRIPTION,
        {
          studentId,
          profileDesc: description,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      setSnackbar({ open: true, message: res.data.message || "Updated successfully", severity: "success" });
      setEditMode(false);
    } catch (err) {
      console.error("Failed to update description", err);
      setSnackbar({ open: true, message: "Failed to update description.", severity: "error" });
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleChange = (e) => {
    // Enforce max total characters
    if (e.target.value.length <= MAX_TOTAL_CHARS) {
      setDescription(e.target.value);
    }
  };

  return (
    <Box mt={2} maxWidth={520}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="h6">Profile Description</Typography>
        {!editMode ? (
          <Tooltip title="Edit Description" arrow>
            <IconButton onClick={() => setEditMode(true)}>
              <EditIcon />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title="Save Description" arrow>
            <IconButton onClick={handleSaveDescription} disabled={description.trim().length === 0}>
              <SaveIcon color="success" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {!editMode ? (
        <Typography
          fontSize={14}
          sx={{
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            overflowWrap: "break-word",
            px: 1,
            lineHeight: 1.5,
          }}
        >
          {description || "No profile description available."}
        </Typography>
      ) : (
        <>
          <TextField
            multiline
            fullWidth
            minRows={4}
            value={description}
            onChange={handleChange}
            placeholder="Enter your profile description..."
            inputProps={{
              maxLength: MAX_TOTAL_CHARS,
              style: {
                fontFamily: "monospace",
                fontSize: 14,
                lineHeight: 1.5,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                overflowWrap: "break-word",
              },
            }}
          />
          <Typography
            variant="caption"
            color={description.length > MAX_TOTAL_CHARS ? "error" : "textSecondary"}
            sx={{ mt: 0.5, display: "block", textAlign: "right" }}
          >
            {description.length} / {MAX_TOTAL_CHARS} characters
          </Typography>
        </>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <MuiAlert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
          elevation={6}
          variant="filled"
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
};

ProfileDescription.propTypes = {
  studentId: PropTypes.number.isRequired,
  token: PropTypes.string.isRequired,
};

export default ProfileDescription;
